import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { useBreadcrumb } from '@/shared/context/BreadcrumbContext'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  ArrowLeft, Eye, PlayCircle, CheckCircle2, TrendingUp, TrendingDown, RefreshCw,
} from 'lucide-react'
import { useReporteEstadisticas } from '../hooks/useReportes'
import { MetricCard } from '../components/reportes/MetricCard'
import { StatsChart } from '../components/reportes/StatsChart'

export function ReporteEstadisticasPage() {
  useDocumentTitle('Estadísticas de Encuesta')
  const { id } = useParams<{ id: string }>()
  const encuestaId = Number(id)
  const navigate = useNavigate()
  const { setOverride, clearOverride } = useBreadcrumb()

  const { data, isLoading, refetch } = useReporteEstadisticas(encuestaId)

  useEffect(() => {
    if (data?.encuesta_titulo) {
      setOverride(`/encuestas/reportes/${id}`, data.encuesta_titulo)
    }
    return () => { clearOverride(`/encuestas/reportes/${id}`) }
  }, [data?.encuesta_titulo, id, setOverride, clearOverride])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/encuestas/reportes')}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">{data.encuesta_titulo} - Estadísticas</h1>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" /> Actualizar
        </Button>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <MetricCard label="Vistas" value={data.total_vistas} icon={Eye} />
        <MetricCard label="Inicios" value={data.total_inicios} icon={PlayCircle} />
        <MetricCard label="Terminados" value={data.total_terminados} icon={CheckCircle2} />
        <MetricCard label="Conversión" value={`${data.tasa_conversion.toFixed(1)}%`} icon={TrendingUp} />
        <MetricCard label="Abandono" value={`${data.tasa_abandono.toFixed(1)}%`} icon={TrendingDown} />
      </div>

      {/* Chart */}
      <StatsChart data={data.stats_diarias} />

      {/* Daily stats table */}
      {data.stats_diarias.length > 0 && (
        <Card className="border border-gray-300 dark:border-gray-700">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Datos diarios</h3>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-center">Vistas</TableHead>
                  <TableHead className="text-center">Inicios</TableHead>
                  <TableHead className="text-center">Terminados</TableHead>
                  <TableHead className="text-center">Conversión</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.stats_diarias.map((stat) => (
                  <TableRow key={stat.fecha}>
                    <TableCell>{new Date(stat.fecha).toLocaleDateString('es-EC')}</TableCell>
                    <TableCell className="text-center">{stat.vistas}</TableCell>
                    <TableCell className="text-center">{stat.inicios}</TableCell>
                    <TableCell className="text-center">{stat.terminados}</TableCell>
                    <TableCell className="text-center">{stat.tasa_conversion.toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  )
}
